import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProjectSummary from './ProjectSummary';
import ProjectTimeline from './ProjectTimeline';
import PhaseProgress from './PhaseProgress';
import LatestUpdates from './LatestUpdates';
import UpdateDetail from './UpdateDetail';
import EditOverallProgressDialog from './EditOverallProgressDialog';
import EditPhaseProgressDialog from './EditPhaseProgressDialog';
import Dokumentacija from './Dokumentacija';
import MuiDialog from '../../../UI/MuiDialog/MuiDialog';
import { getProgresField, mapProgresData } from '../data/mapProgresData';
import styles from './ProjectContent.module.css';

const ProjectContent = ({
  progresData,
  updates = [],
  onSaveProgres,
  onRefreshUpdates,
}) => {
  const { t } = useTranslation();
  const [selectedUpdate, setSelectedUpdate] = useState(null);
  const [overallDialogOpen, setOverallDialogOpen] = useState(false);
  const [phaseDialogOpen, setPhaseDialogOpen] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [docsDialogOpen, setDocsDialogOpen] = useState(false);
  const [docsPhase, setDocsPhase] = useState(null);
  const [saving, setSaving] = useState(false);
  const mapped = useMemo(() => mapProgresData(progresData), [progresData]);

  useEffect(() => {
    setSelectedUpdate(updates?.length ? updates[0] : null);
  }, [updates]);

  const project = mapped?.project ?? null;
  const phases = mapped?.phases ?? [];

  const handlePhaseClick = (phase) => {
    if (!progresData || !phase?.prikaz) return;
    setDocsPhase(phase);
    setDocsDialogOpen(true);
  };

  const handleOverallConfirm = async (values) => {
    setSaving(true);
    const success = await onSaveProgres(values);
    setSaving(false);

    if (success) {
      setOverallDialogOpen(false);
    }
  };

  const handlePhaseConfirm = async (progres) => {
    if (!selectedPhase) return;

    setSaving(true);
    const success = await onSaveProgres({
      [`${selectedPhase.key}Progres`]: progres,
    });
    setSaving(false);

    if (success) {
      setPhaseDialogOpen(false);
      setSelectedPhase(null);
    }
  };

  const closeDocsDialog = () => {
    setDocsDialogOpen(false);
    setDocsPhase(null);
  };

  return (
    <div>
      <ProjectSummary
        project={project}
        onEdit={
          progresData && localStorage.idFirme === '1'
            ? () => setOverallDialogOpen(true)
            : undefined
        }
      />

      <ProjectTimeline
        phases={phases}
        onPhaseProgressClick={
          progresData && localStorage.idFirme === '1'
            ? (phase) => {
                if (!phase.prikaz) return;
                setSelectedPhase(phase);
                setPhaseDialogOpen(true);
              }
            : undefined
        }
        onPhaseClick={progresData ? handlePhaseClick : undefined}
      />

      <div className={styles.bottomGrid}>
        <div className={styles.bottomGridItem}>
          <PhaseProgress
            phases={phases}
            onPhaseClick={progresData ? handlePhaseClick : undefined}
          />
        </div>

        <div className={styles.bottomGridItem}>
          <LatestUpdates
            updates={updates}
            selectedId={selectedUpdate?.id}
            onSelect={setSelectedUpdate}
          />
        </div>

        <div className={styles.bottomGridItem}>
          <UpdateDetail
            update={selectedUpdate}
            hasData={!!progresData || updates.length > 0}
            onClose={() => setSelectedUpdate(null)}
          />
        </div>
      </div>

      <EditOverallProgressDialog
        open={overallDialogOpen}
        handleClose={() => setOverallDialogOpen(false)}
        handleConfirm={handleOverallConfirm}
        initialProgres={getProgresField(progresData, 'ukupanProgres')}
        initialDatum={getProgresField(progresData, 'predvidjeniDatumIzrada')}
        loading={saving}
      />

      <EditPhaseProgressDialog
        open={phaseDialogOpen}
        handleClose={() => {
          setPhaseDialogOpen(false);
          setSelectedPhase(null);
        }}
        handleConfirm={handlePhaseConfirm}
        phaseLabel={
          selectedPhase ? t(`projectPhases.${selectedPhase.key}`) : ''
        }
        initialProgres={
          selectedPhase
            ? getProgresField(progresData, `${selectedPhase.key}Progres`)
            : null
        }
        loading={saving}
      />

      <MuiDialog
        maxWidth="xl"
        open={docsDialogOpen}
        handleClose={closeDocsDialog}
        title={docsPhase ? t(`projectPhases.${docsPhase.key}`) : t('document')}
        deniedText={t('close')}
        content={
          docsPhase ? (
            <Dokumentacija
              key={`${docsPhase.key}-${docsPhase.metadata ?? docsPhase.idPPF}`}
              idMasine={getProgresField(progresData, 'idMasine') ?? null}
              metadataFaze={docsPhase.metadata ?? null}
              aktiviranProjekat
              onDokumentiChanged={onRefreshUpdates}
            />
          ) : null
        }
      />
    </div>
  );
};

export default ProjectContent;
